import { useMediaQuery } from "@mantine/hooks";
import { useAgentChatDrawerWidth } from "@src/components/agent-chat/hooks/use-agent-chat-drawer-width";
import clsx from "clsx";
import { motion } from "framer-motion";
import {
	type CSSProperties,
	type PropsWithChildren,
	useEffect,
	useState,
} from "react";

type AgentChatDrawerShellProps = PropsWithChildren<{
	enterFromFullscreen?: boolean;
}>;

/** Matches Tailwind `md` (push layout + resize). Below: full-width overlay. */
const DESKTOP_DRAWER_MEDIA = "(min-width: 768px)";

/**
 * Live width caps via CSS (viewport / sidebar / min board) — no resize
 * listeners. Preferred width comes from localStorage + drag.
 */
const PUSH_DRAWER_MAX_WIDTH =
	"min(50vw, var(--illumi-agent-chat-max-width), calc(100vw - var(--illumi-sidebar-occupied, 0px) - var(--illumi-agent-chat-min-board)))";

export function AgentChatDrawerShell({
	children,
	enterFromFullscreen = false,
}: AgentChatDrawerShellProps) {
	// Read media immediately so the first paint is desktop on md+, avoiding a
	// mobile `x` slide that leaves translateX stuck after switching branches.
	const isDesktop = useMediaQuery(DESKTOP_DRAWER_MEDIA, true, {
		getInitialValueInEffect: false,
	});
	const { drawerRef, widthPx, handleResizeMouseDown } =
		useAgentChatDrawerWidth();
	// CSS width (not Framer) — FM width animation was sticking at 0px after
	// open under AnimatePresence. Expand on the next frame for enter motion.
	const [openWidthPx, setOpenWidthPx] = useState(
		enterFromFullscreen ? widthPx : 0,
	);

	useEffect(() => {
		if (enterFromFullscreen) {
			setOpenWidthPx(widthPx);
			return;
		}
		const frame = requestAnimationFrame(() => {
			setOpenWidthPx(widthPx);
		});
		return () => cancelAnimationFrame(frame);
	}, [enterFromFullscreen, widthPx]);

	const slideTransition = enterFromFullscreen
		? { duration: 0 }
		: {
				type: "spring" as const,
				damping: 25,
				stiffness: 300,
			};

	const pushStyle = {
		width: openWidthPx,
		maxWidth: PUSH_DRAWER_MAX_WIDTH,
		minWidth: 0,
		transition: enterFromFullscreen ? undefined : "width 180ms ease-out",
	} satisfies CSSProperties;

	if (!isDesktop) {
		return (
			<motion.div
				key="agent-chat-drawer-mobile"
				className={clsx(
					"drawer fixed inset-0",
					"border-l border-border-subtle bg-surface-default",
					"flex h-dvh min-h-0 flex-col overflow-hidden",
					"w-full",
					"z-(--illumi-z-index-right-drawer)",
					"select-text",
				)}
				initial={enterFromFullscreen ? { x: 0 } : { x: "100%" }}
				animate={{ x: 0 }}
				exit={{ x: "100%" }}
				transition={slideTransition}
				onPointerDown={(event) => {
					event.stopPropagation();
				}}
				onKeyDown={(event) => {
					event.stopPropagation();
				}}
				onKeyUp={(event) => {
					event.stopPropagation();
				}}
			>
				<div
					className="flex h-full min-h-0 flex-col"
					onContextMenu={(event) => {
						event.stopPropagation();
					}}
				>
					{children}
				</div>
			</motion.div>
		);
	}

	return (
		<motion.div
			key="agent-chat-drawer-desktop"
			ref={drawerRef}
			className={clsx(
				"drawer relative group/agent-chat-drawer",
				"border-l border-border-subtle bg-surface-default",
				"flex h-full min-h-0 shrink-0 flex-col overflow-hidden",
				"select-text",
			)}
			style={pushStyle}
			initial={{ x: 0 }}
			animate={{ x: 0 }}
			exit={{ x: 0 }}
			onPointerDown={(event) => {
				event.stopPropagation();
			}}
			onKeyDown={(event) => {
				event.stopPropagation();
			}}
			onKeyUp={(event) => {
				event.stopPropagation();
			}}
		>
			<div
				className="flex h-full min-h-0 flex-col"
				onContextMenu={(event) => {
					event.stopPropagation();
				}}
			>
				{children}
			</div>

			{/* Left-edge resize handle — match sidebar nav handle */}
			<div
				onMouseDown={handleResizeMouseDown}
				className={clsx(
					"absolute top-0 -left-1 z-10 h-full w-2",
					"cursor-col-resize group/handle",
				)}
			>
				<div
					className={clsx(
						"flex h-full w-full items-center justify-center",
						"opacity-0 transition-all",
						"group-hover/agent-chat-drawer:opacity-100",
						"group-hover/handle:bg-brand-primary/30",
					)}
				>
					<div
						className={clsx(
							"h-12 w-[1.5px] rounded-full",
							"bg-brand-primary opacity-40",
							"group-hover/handle:opacity-100",
						)}
					/>
				</div>
			</div>
		</motion.div>
	);
}
